import {
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import React, { useEffect, useState } from 'react'

import { getKekaPunctuality } from 'api/kekaAttendance'
import { lateAfterGrace } from 'app/pages/Teacher/Attendance/Attendance'

import {
  SECTIONS,
  band,
  counts,
  dayFor,
  isPartTime,
  isPreschool,
  monthDays,
  prevMonth,
  punctuality,
  weekDays
} from './scoring'

// The Punctuality calendar: the Teacher Scorecard pop-up's first section, and
// on its own the pop-up behind a name on Teacher > Attendance (Satya, 22 Oct:
// "the same view, just the punctuality part"). One component, so the two
// never drift.

export const C = {
  navy: '#282E3C',
  band: '#F6F3EE',
  line: '#ECE6DE',
  ink2: 'rgba(0,0,0,.56)',
  good: '#2F7D4F',
  goodBg: '#E8F5EC',
  ok: '#A86A12',
  okBg: '#FCF1DE',
  weak: '#B4441C',
  weakBg: '#FCEDE6',
  leave: '#3172C6',
  leaveBg: '#E8F0FA',
  absent: '#8E1F35',
  absentBg: '#F8E7EB'
}

const MONTHS_BACK = 6
const LAST4 = 'last4'

const useStyles = makeStyles(() => ({
  scroll: { overflowX: 'auto' },
  calc: { color: C.ink2, fontSize: 12.5, marginBottom: 8 },
  cal: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(72px, 1fr))',
    gap: 4,
    minWidth: 540
  },
  day: {
    borderRadius: 6,
    padding: '4px 7px',
    fontSize: 11,
    lineHeight: 1.3,
    minHeight: 44
  },
  dayDate: { color: C.ink2, display: 'block' },
  dayMain: { display: 'block', fontWeight: 700, fontSize: 12 },
  on: { background: C.goodBg, color: C.good },
  late: { background: C.okBg, color: C.ok, border: `1px solid ${C.ok}` },
  absent: { background: C.absentBg, color: C.absent },
  leave: { background: C.leaveBg, color: C.leave },
  off: { border: '1px dashed #ddd', color: C.ink2 },
  blank: { background: '#f5f5f5', color: C.ink2 },
  soon: { border: '1px dashed #e6e6e6', color: 'rgba(0,0,0,.35)' },
  legend: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginTop: 8,
    fontSize: 12
  },
  legendItem: { borderRadius: 6, padding: '2px 8px' },
  // pop-up
  dHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 8
  },
  sub: { color: C.ink2, fontSize: 12 },
  period: { minWidth: 190, background: '#fff' },
  sec: { borderTop: `1px solid ${C.line}`, paddingTop: 14, marginTop: 14 },
  secHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6
  },
  secTitle: {
    fontWeight: 700,
    fontSize: 15,
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  dot: { width: 9, height: 9, borderRadius: '50%', display: 'inline-block' },
  pill: {
    display: 'inline-block',
    minWidth: 40,
    textAlign: 'center',
    borderRadius: 999,
    padding: '2px 9px',
    fontWeight: 700,
    fontSize: 13
  },
  good: { background: C.goodBg, color: C.good },
  ok: { background: C.okBg, color: C.ok },
  weak: { background: C.weakBg, color: C.weak },
  none: { color: C.ink2, fontWeight: 400 },
  loading: { padding: 40, textAlign: 'center' },
  empty: { padding: 30, textAlign: 'center', color: C.ink2 }
}))

export const fmtDate = (iso, opts) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', opts)
const shortDate = (iso) => fmtDate(iso, { day: 'numeric', month: 'short' })

function DayBox({ iso, v, k, kekaLatest, hadClass, classes }) {
  const day = fmtDate(iso, { weekday: 'short', day: 'numeric' })
  if (kekaLatest && iso > kekaLatest)
    return (
      <div className={`${classes.day} ${classes.soon}`}>
        <span className={classes.dayDate}>{day}</span>
        <span className={classes.dayMain}>Not in Keka yet</span>
      </div>
    )
  // Absent or on leave with no class that day is not a missed day: shown,
  // not scored.
  if ((k === 'absent' || k === 'leave') && !hadClass)
    return (
      <Tooltip title="No class in Alfred that day, so not counted">
        <div className={`${classes.day} ${classes.off}`}>
          <span className={classes.dayDate}>{day}</span>
          <span className={classes.dayMain}>
            {k === 'leave' ? 'Leave' : 'Absent'} · no class
          </span>
        </div>
      </Tooltip>
    )
  if (k === 'holiday')
    return (
      <Tooltip title="Holiday on the preschool calendar, so not counted">
        <div className={`${classes.day} ${classes.off}`}>
          <span className={classes.dayDate}>{day}</span>
          <span className={classes.dayMain}>Holiday</span>
        </div>
      </Tooltip>
    )
  // Every word here is a legend item below.
  const main = {
    on: v && v.in,
    late: v && v.in,
    absent: 'Absent',
    leave: 'Leave',
    off: v && v.s === 'H' ? 'Holiday' : 'Week off',
    none: 'No Keka row'
  }[k]
  return (
    <Tooltip title={v && v.st ? `Shift starts ${v.st}` : ''}>
      <div className={`${classes.day} ${classes[k === 'none' ? 'blank' : k]}`}>
        <span className={classes.dayDate}>{day}</span>
        <span className={classes.dayMain}>{main}</span>
        {k === 'late' && <span>{lateAfterGrace(v)} min late</span>}
      </div>
    </Tooltip>
  )
}

// How the score is worked out, the Mon–Sun calendar and its legend. `t` is a
// scorecard teacher row (or the same shape from the punctuality endpoint).
export function PunctualitySection({ t, dates, kekaLatest }) {
  const classes = useStyles()
  const pun = punctuality(t, dates)
  // A month can start mid-week: blank boxes keep the days under Mon–Sun.
  const lead = (new Date(`${dates[0]}T00:00:00`).getDay() + 6) % 7
  return (
    <>
      <div className={classes.calc}>
        Keka · on time on {pun.on} of {pun.worked} working days
        {pun.absent || pun.leave
          ? ` (incl. ${[
              pun.absent ? `${pun.absent} absent` : '',
              pun.leave ? `${pun.leave} on leave` : ''
            ]
              .filter(Boolean)
              .join(' and ')}${
              isPartTime(t) && t.class_days !== null ? ' on a class day' : ''
            })`
          : ''}
        {pun.score !== null
          ? ` → ${pun.on}/${pun.worked} × 5 = ${pun.score.toFixed(1)}`
          : ''}{' '}
        · week off and {isPreschool(t) ? 'preschool calendar ' : ''}
        holidays not counted
        {!isPartTime(t)
          ? ' · full-time, so every absent and leave day counts'
          : t.class_days === null
          ? ' · not linked to an Alfred teacher, so every absent and leave day counts'
          : ' · part-time, so absent or leave counts only on a day with a class in Alfred'}
      </div>
      <div className={classes.scroll}>
        <div className={classes.cal}>
          {Array.from({ length: lead }, (_, i) => (
            <div key={`lead${i}`} />
          ))}
          {dates.map((d) => (
            <DayBox
              key={d}
              iso={d}
              v={t.days[d]}
              k={dayFor(t, d)}
              kekaLatest={kekaLatest}
              hadClass={counts(t, d)}
              classes={classes}
            />
          ))}
        </div>
      </div>
      <div className={classes.legend}>
        {[
          ['on', 'On time (clock-in)'],
          ['late', 'Late'],
          ['absent', 'Absent (counted)'],
          ['leave', 'Leave (counted)'],
          ['off', 'Absent / Leave · no class (not counted)'],
          ['off', 'Week off'],
          ['off', 'Holiday'],
          ['blank', 'No Keka row'],
          ['soon', 'Not in Keka yet']
        ].map(([k, label]) => (
          <span key={label} className={`${classes.legendItem} ${classes[k]}`}>
            {label}
          </span>
        ))}
      </div>
    </>
  )
}

const todayIso = () => {
  const d = new Date()
  const pad = (x) => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
const monthName = (ym) =>
  new Date(`${ym}-01T00:00:00`).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric'
  })

// The scorecard's periods: the last 4 weeks, or one calendar month.
const periodDays = (period, weeks) => {
  if (period === LAST4) {
    const now = weeks.slice(-4).flatMap(weekDays)
    return {
      now,
      label: `last 4 weeks, ${shortDate(now[0])} – ${shortDate(
        now[now.length - 1]
      )}`
    }
  }
  const today = todayIso()
  return {
    now: monthDays(period, today),
    label: `${monthName(period)}${
      today.slice(0, 7) === period ? ' (so far)' : ''
    }`
  }
}

// Teacher > Attendance: a name's pop-up, a whole month by default (Satya,
// 22 Oct). `person` = { no, name, job, dept }, `centre` = the centre label the
// page shows for her, `attendance(person, dates)` = the page's own attendance
// figures, drawn above the scorecard's Punctuality section. The two use
// different rules (absent always counts there; here only on a class day), so
// each is headed.
export function PunctualityDialog({ person, centre, onClose, attendance }) {
  const classes = useStyles()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [period, setPeriod] = useState(todayIso().slice(0, 7))

  useEffect(() => {
    let cancelled = false
    getKekaPunctuality({ no: person.no })
      .then((d) => !cancelled && setData(d))
      .catch((err) => {
        if (cancelled) return
        const detail =
          err && err.response && err.response.data && err.response.data.detail
        setError(detail || 'Could not load punctuality. Please retry.')
      })
    return () => {
      cancelled = true
    }
  }, [person.no])

  const months = [todayIso().slice(0, 7)]
  while (months.length < MONTHS_BACK)
    months.push(prevMonth(months[months.length - 1]))

  const span = data && periodDays(period, data.weeks)
  const t = data && data.person
  const score = t && span.now.length ? punctuality(t, span.now).score : null
  const sec = SECTIONS.find((x) => x.key === 'pun')

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <div className={classes.dHead}>
          <div>
            <Typography variant="h6">{person.name}</Typography>
            <div className={classes.sub}>
              {[
                person.no,
                centre,
                person.job || person.dept,
                span && span.label
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TextField
              select
              size="small"
              variant="outlined"
              label="Period"
              className={classes.period}
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {months.map((m) => (
                <MenuItem key={m} value={m}>
                  {monthName(m)}
                </MenuItem>
              ))}
              <MenuItem value={LAST4}>Last 4 weeks</MenuItem>
            </TextField>
            <IconButton aria-label="Close" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        {error && <div className={classes.empty}>{error}</div>}
        {!error && !data && (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        )}
        {t && attendance && (
          <div className={classes.sec}>
            <div className={classes.secHead}>
              <span className={classes.secTitle}>Attendance</span>
            </div>
            {attendance(t, span.now)}
          </div>
        )}
        {t && (
          <div className={classes.sec}>
            <div className={classes.secHead}>
              <span className={classes.secTitle}>
                <span
                  className={classes.dot}
                  style={{ background: sec.color }}
                />
                {sec.label} (scorecard)
              </span>
              {score === null ? (
                <Tooltip title="No clock-ins in Keka for these weeks">
                  <span className={`${classes.pill} ${classes.none}`}>—</span>
                </Tooltip>
              ) : (
                <span className={`${classes.pill} ${classes[band(score)]}`}>
                  {score.toFixed(1)}
                </span>
              )}
            </div>
            {span.now.length ? (
              <PunctualitySection
                t={t}
                dates={span.now}
                kekaLatest={data.keka_latest}
              />
            ) : (
              <div className={classes.empty}>No days in this period yet.</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
